import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
} from 'lucide-react';
import { createExpense, getExpenseCategories } from '../../../../services/finance_services/expenseService';
import { uploadExpenseAttachment } from '../../../../services/finance_attachment_services/expenseAttachmentService';
import ReportForm from '../../../../components/ReportForm';
import { Paperclip } from 'lucide-react';
import Message from '../../../../components/Message';
import useApi from '../../../../hooks/useApi';

const AddInviterExpensePage = () => {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dateOfExpense, setDateOfExpense] = useState('');
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [vendor, setVendor] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const { loading, error, successMessage, callApi, setError, setSuccessMessage } = useApi(createExpense, {showSuccessToast: true, msg:"New expense report created"});

  // Load expense categories directly
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const catResp = await getExpenseCategories(invitationId);
        setCategories(catResp.data);
      } catch (err) {
        setError(err.message || err);
      }
    };
    fetchCategories();
  }, [invitationId, setError]);

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    const expenseData = {
      title,
      description,
      amount,
      date_of_expense: dateOfExpense,
      category,
      payment_method: paymentMethod,
      vendor,
      receipt_number: receiptNumber,
    };

    try {
      // First create the expense
      console.log({
        'expenseData': expenseData,
        'invitationId': invitationId
      })
      const createdExpense = await callApi(expenseData, invitationId);

      // Then handle attachments if any
      if (files.length > 0) {
        setUploadingAttachments(true);
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name);
            return uploadExpenseAttachment(createdExpense.id, formData, invitationId);
          })
        );
        setUploadingAttachments(false);
      }

      setSuccessMessage('Expense created successfully!');
      setTimeout(() => {
        navigate(`/dashboard/collaborator/${invitationId}/expenses`);
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const formFields = [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      value: title,
      onChange: (e) => setTitle(e.target.value),
      required: true,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      value: description,
      onChange: (e) => setDescription(e.target.value),
    },
    {
      name: 'amount',
      label: 'Amount',
      type: 'number',
      value: amount,
      onChange: (e) => setAmount(e.target.value),
      required: true,
    },
    {
      name: 'date_of_expense',
      label: 'Date of Expense',
      type: 'date',
      value: dateOfExpense,
      onChange: (e) => setDateOfExpense(e.target.value),
      required: true,
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      value: category,
      onChange: (e) => setCategory(e.target.value),
      options: categories,
      required: true,
    },
    {
      name: 'payment_method',
      label: 'Payment Method',
      type: 'text',
      value: paymentMethod,
      onChange: (e) => setPaymentMethod(e.target.value),
    },
    {
      name: 'vendor',
      label: 'Vendor',
      type: 'text',
      value: vendor,
      onChange: (e) => setVendor(e.target.value),
    },
    {
      name: 'receipt_number',
      label: 'Receipt Number',
      type: 'text',
      value: receiptNumber,
      onChange: (e) => setReceiptNumber(e.target.value),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/dashboard/collaborator/${invitationId}/expenses`)}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Expenses
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Add New Expense</h1>
      </div>

      {/* Display API Error Message */}
      <Message type="error" message={error} />

      {/* Display Success Message */}
      <Message type="success" message={successMessage} />

      {/* Expense Form */}
      <ReportForm
        title="Add New Expense"
        fields={formFields}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/dashboard/collaborator/${invitationId}/expenses`)}
        loading={loading}
        submitButtonText="Add Expense"
      >
        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700">
            Attachments (Receipts, Invoices, etc.)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                >
                  <span>Upload files</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, JPG, PNG up to 10MB
              </p>
            </div>
            {files.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-500">Selected files:</p>
                <ul className="mt-1 text-sm text-gray-700">
                  {Array.from(files).map((file, index) => (
                    <li key={index} className="flex items-center">
                      <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                      {file.name} ({Math.round(file.size / 1024)} KB)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ReportForm>
    </div>
  );
};

export default AddInviterExpensePage;